import {
  useApolloClient,
  useSubscription as useApolloSubscription,
} from "@apollo/client";
import { get } from "lodash";
import { useMemo, useState } from "react";
import { useQuery } from "shared/hooks/useApi";
import { CALLS } from "utils/api/graphql/queries/call";
import { CONTACT } from "utils/api/graphql/queries/contacts";
import { EMAIL_LOGS } from "utils/api/graphql/queries/email-log";
import { QUALIFIERS } from "utils/api/graphql/queries/qualifiers";
import { USER_ACTIVITY } from "utils/api/graphql/queries/user-activity";
import graphql from "utils/api/graphql";
import { ALVIO_ACTIVITY_ACTIONS } from "utils/constants";
import {
  buildContactActivityBuckets,
  countQualificationActivitiesFromQualifiers,
} from "../utils/buildContactActivitiesList";

const CALLS_PAGE_SIZE = 10;
const PROJECT_ACTIVITIES_TAKE = 150;
const EMAIL_LOGS_TAKE = 150;

/**
 * Lifecycle actions stored on the UserActivity table that drive the Statuts
 * bucket (and the unified Activités timeline). Quotes / subscriptions are no
 * longer queried separately — every milestone is recorded as a UserActivity
 * row by the API (`recordEventInitiator`, distribution / takeover handlers,
 * signature webhook), so the timeline reads from a single source.
 */
const SYSTEM_ACTIVITY_ACTIONS = [
  "projectCreated",
  "quoteSent",
  "quoteFollowUpSent",
  "signatureInitiated",
  "sentToManagement",
  "sentToManagementManually",
  "projectDuplicated",
  "projectCreatedFromSource",
  "projectSourceChanged",
  "projectDistributed",
  "projectTakenOver",
  // "documentsSigned",
  "electronicSignatureCompleted",
  "manualSignatureCompleted",
  "emailDelivered",
  "emailOpened",
  "emailClicked",
  "documentOpened",
  ...Object.values(ALVIO_ACTIVITY_ACTIONS),
];

const SYSTEM_ACTIONS_SET = new Set(SYSTEM_ACTIVITY_ACTIONS);

const isSystemRow = (row) => SYSTEM_ACTIONS_SET.has(get(row, "action"));

/**
 * Loads the activity buckets used in the contact drawer Activities tab
 * (qualifications, statuts, appels, emails) for a given project + contact.
 *
 * Emails come from the email log table: every email actually sent to the
 * prospect address (To or CC), across projects of the organization. The
 * legacy `UserActivity` `sendEmail` rows are not read anymore to avoid
 * showing the same email twice.
 *
 * CALLS / USER_ACTIVITY / EMAIL_LOGS use `no-cache` so normalized writes do
 * not merge partial `Project` / `Contact` / `User` into the Apollo cache and
 * strip fields still needed by the projects list (e.g. contact name on
 * other rows).
 *
 * `activitiesTotalCount` sums API `count` for calls, project activities and
 * email logs (handles pagination) plus an in-memory count for
 * qualifications aligned with {@link buildContactActivityBuckets}.
 */
const useProjectMergedActivities = ({
  project,
  contactId,
  phoneNumber,
  emailAddress,
  skip = false,
}) => {
  const apolloClient = useApolloClient();
  const [callsDisplayCount, setCallsDisplayCount] = useState(CALLS_PAGE_SIZE);
  const skipAll = skip || !project?.id || !contactId;

  const { data: qualifiersData, loading: qualifiersLoading } = useQuery(
    QUALIFIERS,
    {
      skip: skipAll,
      variables: {
        withDeleted: true,
        where: {
          qualifications: {
            contact: { id: contactId },
          },
        },
      },
    },
  );

  const callsWhere = phoneNumber
    ? { phoneNumber }
    : { contact: { id: contactId } };

  const {
    data: callsData,
    loading: callsLoading,
    refetch: refetchCalls,
  } = useQuery(CALLS, {
    fetchPolicy: "no-cache",
    variables: {
      where: callsWhere,
      orderBy: [{ updatedDate: "DESC" }],
      take: callsDisplayCount,
    },
    skip: skipAll || (!phoneNumber && !contactId),
  });

  const subscriptionWhere = phoneNumber
    ? { phoneNumber }
    : { contact: { id: contactId } };

  const projectId = project?.id;
  const projectActivitiesCustom = useMemo(
    () => ({
      AND: [
        { action: { in: [...SYSTEM_ACTIVITY_ACTIONS] } },
        {
          jsonbPath: {
            column: "data",
            path: ["projectId"],
            op: "=",
            value: projectId,
          },
        },
      ],
    }),
    [projectId],
  );

  const {
    data: projectActivitiesData,
    loading: projectActivitiesLoading,
    refetch: refetchProjectActivities,
  } = useQuery(USER_ACTIVITY, {
    skip: skipAll,
    fetchPolicy: "no-cache",
    variables: {
      take: PROJECT_ACTIVITIES_TAKE,
      orderBy: [{ createdDate: "DESC" }],
      custom: projectActivitiesCustom,
    },
  });

  const skipEmailLogs = skipAll || !emailAddress;

  const {
    data: emailLogsData,
    loading: emailLogsLoading,
    refetch: refetchEmailLogs,
  } = useQuery(EMAIL_LOGS, {
    skip: skipEmailLogs,
    fetchPolicy: "no-cache",
    variables: {
      where: { isExternal: false },
      recipient: emailAddress,
      take: EMAIL_LOGS_TAKE,
      orderBy: [{ createdDate: "DESC" }],
    },
  });

  useApolloSubscription(graphql.subscriptions.EMAIL_LOG_ADDED, {
    variables: { recipient: emailAddress },
    skip: skipEmailLogs,
    onData: () => {
      refetchEmailLogs();
    },
  });

  useApolloSubscription(graphql.subscriptions.CALL_ADDED, {
    variables: { where: subscriptionWhere },
    skip: skipAll || (!phoneNumber && !contactId),
    onData: () => {
      refetchCalls();
      // Refresh the contact so the call counter badge in the drawer header
      // reflects the new countedCallsCount as soon as the call is recorded.
      if (contactId) {
        apolloClient
          .query({
            query: CONTACT,
            variables: { where: { id: contactId } },
            fetchPolicy: "network-only",
          })
          .catch(() => {});
      }
    },
  });

  useApolloSubscription(graphql.subscriptions.USER_ACTIVITY_ADDED, {
    skip: skipAll,
    onData: ({ data: { data: subData } = {} }) => {
      const added = get(subData, "userActivityAdded");
      if (!added || !isSystemRow(added)) return;
      const rowProjectId = get(added, "data.projectId");
      if (rowProjectId !== project?.id) return;
      refetchProjectActivities();
    },
  });

  const isPageLoading = skipAll
    ? false
    : qualifiersLoading ||
      callsLoading ||
      projectActivitiesLoading ||
      emailLogsLoading;

  const systemRows = useMemo(
    () =>
      get(projectActivitiesData, "userActivities.data", []).filter(isSystemRow),
    [projectActivitiesData],
  );

  const buckets = useMemo(() => {
    const qualifiersList = qualifiersData?.qualifiers?.data ?? [];
    const callsList = callsData?.calls?.data ?? [];
    const emailLogsList = emailLogsData?.emailLogs?.data ?? [];
    return buildContactActivityBuckets({
      qualifiers: qualifiersList,
      calls: callsList,
      emailLogs: emailLogsList,
      systemActivityRows: systemRows,
      projectAuthor: project?.author,
    });
  }, [qualifiersData, callsData, emailLogsData, systemRows, project]);

  const activitiesTotalCount = useMemo(() => {
    if (skipAll) return 0;
    const qualifiersList = qualifiersData?.qualifiers?.data ?? [];
    const qualificationTotal =
      countQualificationActivitiesFromQualifiers(qualifiersList);
    const callsTotal = callsData?.calls?.count ?? 0;
    const projectActivitiesTotal =
      projectActivitiesData?.userActivities?.count ?? 0;
    const emailLogsTotal = emailLogsData?.emailLogs?.count ?? 0;
    return (
      qualificationTotal + callsTotal + projectActivitiesTotal + emailLogsTotal
    );
  }, [
    skipAll,
    qualifiersData,
    callsData,
    projectActivitiesData,
    emailLogsData,
  ]);

  const calls = callsData?.calls?.data || [];
  const callsTotalCount = callsData?.calls?.count || 0;
  const hasMoreCalls = calls.length < callsTotalCount;

  const handleLoadMoreCalls = () => {
    setCallsDisplayCount((prev) => prev + CALLS_PAGE_SIZE);
  };

  return {
    isPageLoading,
    activitiesTotalCount,
    ...buckets,
    hasMoreCalls,
    handleLoadMoreCalls,
  };
};

export default useProjectMergedActivities;
